/** @format */

import { useMemo, useState } from "react";
import type { QuestionClassificationProposal, SpecificationThemeAggregation } from "@question-bank/shared";

export interface TaxonomySelection {
	theme: string;
	topic: string;
	subTopic: string;
}

interface ClassificationReviewProps {
	proposal: QuestionClassificationProposal;
	/** Current persisted sub-topic, used to mark the active choice. */
	current?: Partial<TaxonomySelection>;
	/** Full taxonomy to search when the right sub-topic is not in the top 3. */
	taxonomy: SpecificationThemeAggregation[];
	/** Persists the chosen sub-topic (with its theme and topic). */
	onSelect: (selection: TaxonomySelection) => Promise<void>;
}

const MAX_SEARCH_RESULTS = 20;

const formatConfidence = (value: number | undefined): string =>
	typeof value === "number" ? `${Math.round(value * 100)}%` : "n/a";

const flattenTaxonomy = (themes: SpecificationThemeAggregation[]): TaxonomySelection[] =>
	themes.flatMap((theme) =>
		theme.topics.flatMap((topic) =>
			topic.subTopics.map((subTopic) => ({ theme: theme.key, topic: topic.key, subTopic: subTopic.key })),
		),
	);

export const ClassificationReview = ({ proposal, current, taxonomy, onSelect }: ClassificationReviewProps) => {
	const [query, setQuery] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const options = useMemo(() => flattenTaxonomy(taxonomy), [taxonomy]);

	const trimmed = query.trim().toLowerCase();
	const results = useMemo(
		() =>
			trimmed
				? options
						.filter((o) => `${o.theme} ${o.topic} ${o.subTopic}`.toLowerCase().includes(trimmed))
						.slice(0, MAX_SEARCH_RESULTS)
				: [],
		[options, trimmed],
	);

	const choose = async (selection: TaxonomySelection) => {
		setIsSaving(true);
		setError(null);
		try {
			await onSelect(selection);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save selection");
		} finally {
			setIsSaving(false);
		}
	};

	const rows = [
		{ label: "Theme", value: proposal.theme, confidence: proposal.confidence.theme },
		{ label: "Topic", value: proposal.topic, confidence: proposal.confidence.topic },
		{ label: "Sub-topic", value: proposal.subTopic, confidence: proposal.confidence.subTopic },
	];
	const alternatives = (proposal.alternatives ?? []).slice(0, 3);

	return (
		<section aria-label="Classification review" className="space-y-4 rounded border p-4">
			<h2 className="text-lg font-semibold">AI classification proposal</h2>
			<dl className="grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-1">
				{rows.map((row) => (
					<div key={row.label} className="contents">
						<dt className="font-medium">{row.label}</dt>
						<dd>{row.value ?? "-"}</dd>
						<dd aria-label={`${row.label} confidence`}>{formatConfidence(row.confidence)}</dd>
					</div>
				))}
			</dl>

			{alternatives.length > 0 && (
				<div>
					<h3 className="font-medium">Top alternatives</h3>
					<ul className="space-y-1">
						{alternatives.map((alt) => (
							<li key={`${alt.theme}|${alt.topic}|${alt.subTopic}`}>
								<button
									type="button"
									disabled={isSaving}
									aria-pressed={
										current?.theme === alt.theme && current?.topic === alt.topic && current?.subTopic === alt.subTopic
									}
									onClick={() => void choose(alt)}
									className="text-left underline"
								>
									{alt.subTopic} ({formatConfidence(alt.confidence)})
								</button>
								<span className="text-sm text-gray-600">
									{" "}
									{alt.theme} › {alt.topic}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}

			<div>
				<label htmlFor="taxonomy-search" className="font-medium">
					Search all sub-topics
				</label>
				<input
					id="taxonomy-search"
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					className="mt-1 block w-full rounded border px-2 py-1"
				/>
				{trimmed && results.length === 0 && <p className="text-sm">No matching sub-topics</p>}
				{results.length > 0 && (
					<ul aria-label="Taxonomy search results" className="mt-2 space-y-1">
						{results.map((r) => (
							<li key={`${r.theme}|${r.topic}|${r.subTopic}`}>
								<button
									type="button"
									disabled={isSaving}
									onClick={() => void choose(r)}
									className="text-left underline"
								>
									{r.subTopic}
								</button>
								<span className="text-sm text-gray-600">
									{" "}
									{r.theme} › {r.topic}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
			{error && (
				<p role="alert" className="text-red-600">
					{error}
				</p>
			)}
		</section>
	);
};
